import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import GalleryInline from './GalleryInline';
import GalleryLightbox from './GalleryLightbox';
import { buildGallerySlides, useMediaQuery } from './galleryUtils';

import './YetAnotherReactLightbox.css';
import css from './ProductGallery.css';

const EMPTY_IMAGES = [];

const GalleryContent = props => {
  const { title = '', listing, className, getImageAlt } = props;
  const intl = useIntl();
  const images = (listing && listing.images) || EMPTY_IMAGES;
  
  const slides = useMemo(() => buildGallerySlides(images, title, intl, getImageAlt), [
    images,
    title,
    intl,
    getImageAlt,
  ]);
  const [selectedId, setSelectedId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const openingElementRef = useRef(null);
  const rootRef = useRef(null);
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const activeIndex = Math.max(
    0,
    slides.findIndex(slide => slide.id === selectedId)
  );
  const isClosed = Boolean(listing && listing.attributes && listing.attributes.state === 'closed');

  const selectImage = useCallback(
    index => {
      if (slides[index]) setSelectedId(slides[index].id);
    },
    [slides]
  );

  const openGallery = useCallback(
    (event, index) => {
      if (!slides[index]) return;
      openingElementRef.current = event.currentTarget;
      setSelectedId(slides[index].id);
      setIsOpen(true);
    },
    [slides]
  );

  const closeGallery = useCallback(() => setIsOpen(false), []);
  const restoreFocus = useCallback(() => {
    const opener = openingElementRef.current;
    const target = opener && opener.isConnected ? opener : rootRef.current;
    if (target) target.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (!slides.length) {
      setSelectedId(null);
      setIsOpen(false);
    } else if (!slides.some(slide => slide.id === selectedId)) {
      setSelectedId(slides[0].id);
    }
  }, [slides, selectedId]);

  return (
    <section
      ref={rootRef}
      className={[css.root, className].filter(Boolean).join(' ')}
      aria-label={intl.formatMessage({ id: 'ProductGallery.gallery' })}
      tabIndex={-1}
    >
      {isClosed ? <p className={css.unavailable}>{intl.formatMessage({ id: 'ProductGallery.unavailable' })}</p> : null}

      {slides.length ? (
        <GalleryInline
          slides={slides}
          activeIndex={activeIndex}
          onSelect={selectImage}
          onOpen={openGallery}
          suspended={isOpen}
          reducedMotion={reducedMotion}
          title={title}
        />
      ) : (
        <p className={css.empty}>{intl.formatMessage({ id: 'ProductGallery.empty' })}</p>
      )}

      <p className={css.srOnly} role="status" aria-atomic="true">
        {slides.length ? intl.formatMessage({ id: 'ProductGallery.photo' }, { index: activeIndex + 1, count: slides.length }) : ''}
      </p>

      <GalleryLightbox
        slides={slides}
        activeIndex={activeIndex}
        isOpen={isOpen}
        onSelect={selectImage}
        onClose={closeGallery}
        onExited={restoreFocus}
        title={title}
        reducedMotion={reducedMotion}
      />
    </section>
  );
};

const ProductGallery = props => {
  const listing = props.listing;
  const listingId = listing && listing.id && listing.id.uuid;
  return <GalleryContent key={listingId || 'gallery'} {...props} />;
};

export default ProductGallery;
